import { z } from "zod";
import { CreateProjectDto, ProjectFormShape } from "@containers/Project/interfaces";
import { FORM_ERROR_MESSAGES, PROJECT_STATUS, USER_STATUS } from "@shared/constants";
import { Project, User } from "@shared/models";
import { Option } from "@shared/interfaces";
import { actions as stationActions, selectors as stationSelectors } from "@shared/store/";

export const PROJECT_TYPES_OPTIONS: Option<string>[] = [
  { value: "Fixed cost", label: "Fixed cost" },
  { value: "Retained Team", label: "Retained Team" },
  { value: "M&S", label: "M&S" },
];

export const PROJECT_STATUS_OPTIONS: Option[] = [
  { value: PROJECT_STATUS.ACTIVE, label: PROJECT_STATUS.ACTIVE },
  { value: PROJECT_STATUS.COMPLETED, label: PROJECT_STATUS.COMPLETED },
];

const workTypeValidationSchema = z.object({
  id: z.number().optional(),
  work_type_id: z.number({ error: FORM_ERROR_MESSAGES.REQUIRED }),
  hours: z.number({ error: FORM_ERROR_MESSAGES.REQUIRED }),
});

const optionValidationSchema = z.object(
  {
    value: z.string().min(1, FORM_ERROR_MESSAGES.REQUIRED),
    label: z.string().min(1, FORM_ERROR_MESSAGES.REQUIRED),
  },
  { error: FORM_ERROR_MESSAGES.REQUIRED },
);

const AVERAGE_COST_FORMAT = /^\d{1,8}(\.\d{1,2})?$/;

/** Blank (`""` or untouched) is allowed; a number must have at most 8 integer and 2 fraction digits. */
const averageCostValidationSchema = z
  .union([z.number(), z.literal("")])
  .optional()
  .refine((value) => value === undefined || value === "" || AVERAGE_COST_FORMAT.test(String(value)), {
    error: "Please enter correct average cost",
  });

export const validationSchema = z.object({
  name: z.string().min(1, FORM_ERROR_MESSAGES.REQUIRED).max(250, `Name ${FORM_ERROR_MESSAGES.LONG}`),
  code: z.string().min(1, FORM_ERROR_MESSAGES.REQUIRED).max(35, `Code ${FORM_ERROR_MESSAGES.LONG}`),
  type: z.string({ error: FORM_ERROR_MESSAGES.REQUIRED }).min(1, FORM_ERROR_MESSAGES.REQUIRED),
  average_cost: averageCostValidationSchema,
  status: z.string({ error: FORM_ERROR_MESSAGES.REQUIRED }).min(1, FORM_ERROR_MESSAGES.REQUIRED),
  start_date: z.string({ error: FORM_ERROR_MESSAGES.REQUIRED }).min(1, FORM_ERROR_MESSAGES.REQUIRED),
  manager: optionValidationSchema,
  work_types: z.array(workTypeValidationSchema),
}) satisfies z.ZodType<ProjectFormShape>;

export const getWorkTypesInitValues = (project: Project | null) => {
  if (!project || !project.work_types) return [];

  return project.work_types.map((wt) => ({
    id: wt.id,
    work_type_id: wt.work_type_id,
    hours: wt.hours,
  }));
};

export const getManagerInitValues = (project: Project | null) => {
  if (!project || !project.manager) return null;

  return {
    label: `${project.manager.first_name} ${project.manager.last_name}`,
    value: String(project.manager.id),
  };
};

export const getInitValues = (project: Project | null): ProjectFormShape => ({
  name: project?.name || "",
  code: project?.code || "",
  average_cost: project?.average_cost ? Number(project.average_cost) : undefined,
  status: project?.status || PROJECT_STATUS.ACTIVE,
  type: project?.type || PROJECT_TYPES_OPTIONS[0].value,
  start_date: project?.start_date ? new Date(project.start_date).toISOString().substring(0, 10) : undefined,
  work_types: getWorkTypesInitValues(project),
  manager: getManagerInitValues(project),
});

export const prepareSubmitValue = (value: z.output<typeof validationSchema>): CreateProjectDto => ({
  name: value.name,
  code: value.code,
  type: value.type,
  average_cost: value.average_cost ? value.average_cost : null,
  status: value.status,
  start_date: new Date(value.start_date),
  manager_id: Number(value.manager.value),
  work_types: value.work_types,
});

export const handlers = {
  user: {
    selectData: stationSelectors.getUsers,
    loadingAction: stationActions.getUsers,
    prepareOptionFunction: (item: User): Option => {
      return { label: `${item.first_name} ${item.last_name}`, value: String(item.id) };
    },
    getData: (search: string) => {
      return stationActions.getUsers.request({
        search,
        page: 0,
        limit: 500,
        status: USER_STATUS.ACTIVE,
      });
    },
  },
};
